import { StandardInMemoryTable } from "@/components/in-memory-table/StandardInMemoryTable";
import { useSelectedRowIds } from "@/components/query-table/hooks/useSelectedRowIds";
import { useSelectedRowSnapshotsStore } from "@/components/query-table/stores/selectedRowSnapshotsStore";
import { QueryTableProps } from "@/components/query-table/QueryTable.shared";
import { DataTableFeatures } from "@/types/dataTableSettings";
import { useMemo } from "react";

interface QueryTableSelectedOnlyViewProps<T extends { id: string }> {
  listProps: QueryTableProps<T>;
  tableFeaturesMerged: DataTableFeatures;
}

export function QueryTableSelectedOnlyView<T extends { id: string }>({
  listProps,
  tableFeaturesMerged,
}: QueryTableSelectedOnlyViewProps<T>) {
  const {
    name,
    schema,
    translationKey,
    columnOverrides,
    pathBuilder,
    getRowId,
    options,
    children,
    rowSelection,
    setRowSelection,
  } = listProps;
  const snapshotMap = useSelectedRowSnapshotsStore((s) => s.maps[name]);
  const selectedIds = useSelectedRowIds({ rowSelection });

  const selectedRows = useMemo(() => {
    if (!snapshotMap) return [] as T[];

    return [...snapshotMap.entries()]
      .filter(([id]) => selectedIds.has(id))
      .map(([, row]) => row as T);
  }, [snapshotMap, selectedIds]);

  return (
    <StandardInMemoryTable<T>
      schema={schema}
      name={name}
      translationKey={translationKey}
      tableFeatures={tableFeaturesMerged}
      columnOverrides={columnOverrides}
      items={selectedRows}
      options={options}
      getRowId={getRowId}
      pathBuilder={pathBuilder}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
    >
      {children}
    </StandardInMemoryTable>
  );
}
